import { useTheme } from "../context/ThemeContext";
import { useT } from "../i18n";
import type { ThemePreference } from "../lib/theme";

const OPTIONS: ThemePreference[] = ["system", "light", "dark"];

function Icon({ pref }: { pref: ThemePreference }) {
  if (pref === "light") {
    return (
      <svg viewBox="0 0 20 20" fill="currentColor" className="w-3.5 h-3.5" aria-hidden>
        <path d="M10 2a1 1 0 0 1 1 1v1a1 1 0 1 1-2 0V3a1 1 0 0 1 1-1Zm5.657 2.343a1 1 0 0 1 0 1.414l-.707.707a1 1 0 1 1-1.414-1.414l.707-.707a1 1 0 0 1 1.414 0ZM17 9a1 1 0 1 1 0 2h-1a1 1 0 1 1 0-2h1ZM4 9a1 1 0 1 1 0 2H3a1 1 0 1 1 0-2h1Zm11.95 4.95a1 1 0 0 1 0 1.414l-.707.707a1 1 0 0 1-1.414-1.414l.707-.707a1 1 0 0 1 1.414 0ZM6.464 14.536a1 1 0 0 1 0 1.414l-.707.707a1 1 0 1 1-1.414-1.414l.707-.707a1 1 0 0 1 1.414 0ZM10 15a1 1 0 0 1 1 1v1a1 1 0 1 1-2 0v-1a1 1 0 0 1 1-1ZM4.343 5.657a1 1 0 0 1 1.414 0l.707.707A1 1 0 1 1 5.05 7.778l-.707-.707a1 1 0 0 1 0-1.414ZM10 6a4 4 0 1 0 0 8 4 4 0 0 0 0-8Z" />
      </svg>
    );
  }
  if (pref === "dark") {
    return (
      <svg viewBox="0 0 20 20" fill="currentColor" className="w-3.5 h-3.5" aria-hidden>
        <path d="M10.5 2.5a7.5 7.5 0 1 0 7.2 9.6 5.5 5.5 0 1 1-7.2-9.6Z" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" className="w-3.5 h-3.5" aria-hidden>
      <path d="M3 4a1 1 0 0 1 1-1h12a1 1 0 1 1 0 2H4a1 1 0 0 1-1-1Zm0 4a1 1 0 0 1 1-1h12a1 1 0 1 1 0 2H4a1 1 0 0 1-1-1Zm0 4a1 1 0 0 1 1-1h6a1 1 0 1 1 0 2H4a1 1 0 0 1-1-1Zm10 0a3 3 0 1 1 0 6 3 3 0 0 1 0-6Z" />
    </svg>
  );
}

interface Props {
  compact?: boolean;
  className?: string;
}

export default function ThemeToggle({ compact = false, className = "" }: Props) {
  const { preference, setPreference } = useTheme();
  const translate = useT();

  function label(pref: ThemePreference): string {
    switch (pref) {
      case "system":
        return translate("themeSystem");
      case "light":
        return translate("themeLight");
      case "dark":
        return translate("themeDark");
    }
  }

  return (
    <div
      className={`inline-flex rounded-xl bg-surface border border-[var(--border-subtle)] p-0.5 ${className}`}
      role="group"
      aria-label={translate("themeLabel")}
    >
      {OPTIONS.map((option) => {
        const active = preference === option;
        return (
          <button
            key={option}
            type="button"
            onClick={() => setPreference(option)}
            title={label(option)}
            aria-pressed={active}
            className={`inline-flex items-center justify-center gap-1.5 rounded-[10px] text-xs font-medium transition-all ${
              compact ? "h-8 w-8 px-0" : "h-8 px-2.5"
            } ${
              active
                ? "bg-brand-gradient text-white shadow-glow"
                : "text-muted-foreground hover:text-foreground hover:bg-surface-light"
            }`}
          >
            <Icon pref={option} />
            {!compact && <span className="hidden sm:inline">{label(option)}</span>}
          </button>
        );
      })}
    </div>
  );
}
